import ReactDOM from 'react-dom';
import React from 'react';
import { STYLES } from '../components/Styles';
import { updateSessionObject, appendStyleSheet, loadStyles, getFormattedCss } from './Utils';
import debounce from 'lodash/debounce';

// --- CLS prevention via canvas-wide loading overlay ----------------------
// While components inside the GrapesJS canvas iframe asynchronously fetch
// CMS data, images, and fonts, their heights grow from 0 to the natural
// value. That growth pushes siblings down — visible as flicker. We cover
// the canvas with an opaque overlay during this settle period and remove
// it once components have stopped resizing (or a safety timeout fires),
// so the user only ever sees the final, stable layout.
const getCanvasIframe = () => {
  const iframes = document.getElementsByClassName('gjs-frame');
  return iframes && iframes[0] ? iframes[0] : null;
};

const PB_OVERLAY_ID = 'pb-canvas-loading-overlay';
const PB_OVERLAY_QUIET_MS = 600;   // hide once this long passes without a new settle
const PB_OVERLAY_MAX_MS = 6000;          // hard ceiling for render-time install
const PB_OVERLAY_USER_MAX_MS = 8000;     // longer ceiling for user-triggered shows
                                         // (covers website/template change: API fetch + render + settle)

let pbOverlayInstalled = false;
let pbOverlayDismissed = false;
let pbOverlayQuietTimer = null;
let pbOverlayMaxTimer = null;
// Tracks the pending fade-out removal so a re-install within the 260ms
// transition window can cancel the removal and reuse the existing node.
let pbOverlayRemovalTimer = null;

// Suppression window during which no install or force-show is allowed
// (set by the API-failure path so that subsequent unconditional show calls,
// such as the 1.5s re-set in Home.js, don't pop a second loader after the
// error toast has already been shown).
const PB_OVERLAY_SUPPRESS_MS = 3000;
let pbOverlaySuppressUntil = 0;
const isOverlaySuppressed = () => Date.now() < pbOverlaySuppressUntil;

// Lock count for "fetch in progress" — while > 0, neither settle-dismiss
// nor max-timer dismiss can hide the loader. Callers that know they're
// about to trigger a long-running data fetch followed by a re-render
// (e.g. getThemePagesByWebsite during cold load) acquire the lock at the
// start of the fetch and release it in .finally(). This prevents the
// cached-component settle observers from prematurely dismissing the
// loader while real data is still on the wire.
let pbOverlayLockCount = 0;
const isOverlayLocked = () => pbOverlayLockCount > 0;
const lockCanvasOverlay = () => { pbOverlayLockCount++; };
const unlockCanvasOverlay = () => {
  pbOverlayLockCount = Math.max(0, pbOverlayLockCount - 1);
};

const installCanvasOverlay = (timeoutMs) => {
  if (isOverlaySuppressed()) return;
  if (pbOverlayInstalled || pbOverlayDismissed) return;
  const iframe = getCanvasIframe();
  if (!iframe || !iframe.contentDocument || !iframe.contentDocument.body) return;
  const doc = iframe.contentDocument;
  let overlay = doc.getElementById(PB_OVERLAY_ID);
  if (overlay) {
    // A previous overlay node still exists (e.g. dismiss in-flight) — reuse it.
    // Cancel any pending fade-out removal so the still-visible node isn't
    // ripped out from under us 260ms after the prior dismiss started.
    if (pbOverlayRemovalTimer) {
      clearTimeout(pbOverlayRemovalTimer);
      pbOverlayRemovalTimer = null;
    }
    overlay.style.opacity = '1';
  } else {
    overlay = doc.createElement('div');
    overlay.id = PB_OVERLAY_ID;
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'top:0','left:0','right:0','bottom:0',
      'background:radial-gradient(ellipse at center,#ffffff 0%,#f7f8fa 100%)',
      'z-index:2147483647',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'gap:28px',
      'opacity:1',
      'transition:opacity 240ms ease',
      'pointer-events:auto',
      'font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
    ].join(';');
    overlay.innerHTML =
      // Indeterminate progress strip pinned to the top
      '<div style="position:absolute;top:0;left:0;right:0;height:3px;background:rgba(0,152,204,0.08);overflow:hidden;">' +
        '<div style="position:absolute;top:0;left:0;height:100%;width:35%;background:linear-gradient(90deg,transparent 0%,#0098CC 50%,transparent 100%);animation:pb-progress 1.6s ease-in-out infinite;"></div>' +
      '</div>' +
      // Dual-ring spinner: two concentric rings spinning in opposite directions
      '<div style="position:relative;width:64px;height:64px;">' +
        '<div style="position:absolute;inset:0;border-radius:50%;border:3px solid transparent;border-top-color:#0098CC;border-right-color:#0098CC;animation:pb-spin 1.1s cubic-bezier(0.5,0,0.5,1) infinite;"></div>' +
        '<div style="position:absolute;inset:6px;border-radius:50%;border:3px solid transparent;border-bottom-color:rgba(0,152,204,0.35);border-left-color:rgba(0,152,204,0.35);animation:pb-spin 1.6s cubic-bezier(0.5,0,0.5,1) infinite reverse;"></div>' +
      '</div>' +
      // Title + subtitle with subtle pulse
      '<div style="display:flex;flex-direction:column;align-items:center;gap:6px;animation:pb-text-pulse 1.8s ease-in-out infinite;">' +
        '<div style="font-size:15px;font-weight:500;color:#1f2937;letter-spacing:0.2px;">Loading canvas</div>' +
        '<div style="font-size:13px;font-weight:400;color:#6b7280;letter-spacing:0.1px;">Preparing your components…</div>' +
      '</div>' +
      '<style>' +
        '@keyframes pb-spin{to{transform:rotate(360deg);}}' +
        '@keyframes pb-progress{0%{left:-35%;}100%{left:100%;}}' +
        '@keyframes pb-text-pulse{0%,100%{opacity:0.75;}50%{opacity:1;}}' +
      '</style>';
    doc.body.appendChild(overlay);
  }
  pbOverlayInstalled = true;

  const maxMs = typeof timeoutMs === 'number' ? timeoutMs : PB_OVERLAY_MAX_MS;
  if (pbOverlayMaxTimer) clearTimeout(pbOverlayMaxTimer);
  pbOverlayMaxTimer = setTimeout(() => {
    dismissCanvasOverlay();
  }, maxMs);
};

// Force the overlay to show on demand (e.g. when the user picks a website from
// the dropdown, before any new components have rendered). Bypasses the
// dismissed/installed flags so it works regardless of prior state.
const forceShowCanvasOverlay = () => {
  if (isOverlaySuppressed()) return;
  pbOverlayInstalled = false;
  pbOverlayDismissed = false;
  if (pbOverlayQuietTimer) { clearTimeout(pbOverlayQuietTimer); pbOverlayQuietTimer = null; }
  installCanvasOverlay(PB_OVERLAY_USER_MAX_MS);
};

// Dismiss any current loader AND block any further install/force-show calls
// for PB_OVERLAY_SUPPRESS_MS. Used by API-failure paths so the error toast
// is the last thing shown and no later trigger pops a fresh loader.
const suppressCanvasOverlay = () => {
  pbOverlaySuppressUntil = Date.now() + PB_OVERLAY_SUPPRESS_MS;
  dismissCanvasOverlay();
};

const dismissCanvasOverlay = () => {
  if (pbOverlayDismissed) return;
  // While a long fetch is in flight, postpone dismissal — recheck shortly.
  // This applies to both max-timer firings and settle-observer firings.
  if (isOverlayLocked()) {
    if (pbOverlayMaxTimer) clearTimeout(pbOverlayMaxTimer);
    pbOverlayMaxTimer = setTimeout(dismissCanvasOverlay, 1000);
    return;
  }
  pbOverlayDismissed = true;
  if (pbOverlayQuietTimer) { clearTimeout(pbOverlayQuietTimer); pbOverlayQuietTimer = null; }
  if (pbOverlayMaxTimer) { clearTimeout(pbOverlayMaxTimer); pbOverlayMaxTimer = null; }
  const iframe = getCanvasIframe();
  if (!iframe || !iframe.contentDocument) return;
  const overlay = iframe.contentDocument.getElementById(PB_OVERLAY_ID);
  if (!overlay) return;
  overlay.style.opacity = '0';
  if (pbOverlayRemovalTimer) clearTimeout(pbOverlayRemovalTimer);
  pbOverlayRemovalTimer = setTimeout(() => {
    pbOverlayRemovalTimer = null;
    // Guard against a re-install that flipped opacity back to 1 between
    // the dismiss and this timer firing.
    if (overlay.style.opacity !== '0') return;
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }, 260);
};

const scheduleCanvasOverlayDismiss = () => {
  if (!pbOverlayInstalled || pbOverlayDismissed) return;
  // While a long fetch is in flight, don't even schedule — cached
  // components settling early shouldn't dismiss the loader before real
  // data arrives.
  if (isOverlayLocked()) return;
  if (pbOverlayQuietTimer) clearTimeout(pbOverlayQuietTimer);
  pbOverlayQuietTimer = setTimeout(dismissCanvasOverlay, PB_OVERLAY_QUIET_MS);
};

// Poll element height; once it has been stable for several ticks we treat
// the component as settled and push back the canvas overlay's quiet-period
// timer so the overlay only fades after every component has stopped
// resizing. A per-component timeout is the safety net for components that
// never reach stable height.
const observeHeightSettle = (el) => {
  if (!el) return;
  let lastH = el.offsetHeight;
  let stableTicks = 0;
  let attempts = 0;
  const tick = () => {
    if (!el.isConnected) {
      // The view was unmounted (e.g. setComponents reset). Don't dismiss
      // the overlay here — a fresh render() of the replacement view will
      // start a new settle observer that drives dismissal correctly. The
      // overlay's max-timer is the safety net if no replacement comes.
      return;
    }
    const h = el.offsetHeight;
    if (h === lastH) {
      stableTicks++;
    } else {
      stableTicks = 0;
      lastH = h;
    }
    attempts++;
    if (h > 0 && stableTicks >= 4) {
      scheduleCanvasOverlayDismiss();
      return;
    }
    if (attempts > 60) {
      scheduleCanvasOverlayDismiss();
      return;
    }
    setTimeout(tick, 100);
  };
  setTimeout(tick, 100);
};
// -------------------------------------------------------------------------

export default (editor) => {
  const domc = editor.Components;
  const defType = domc.getType('default');
  const defModel = defType.model;
  const wrpChld = 'data-chld';
  var lastExecutedAt = 0;
  var runningUpdateStack = [];
  var stackTimeInterval = [];
  localStorage.setItem( "traitIds", JSON.stringify([]) );

  // Reset the canvas-overlay lifecycle when a new template is loaded
  // (or components are programmatically reset). The next render() call
  // will reinstall the overlay so template switches don't show the
  // mid-load flicker either. Only the quiet timer is cleared — the
  // max-timer is left alone because the next install resets it anyway.
  const resetCanvasOverlayState = () => {
    pbOverlayInstalled = false;
    pbOverlayDismissed = false;
    if (pbOverlayQuietTimer) { clearTimeout(pbOverlayQuietTimer); pbOverlayQuietTimer = null; }
  };
  editor.on('load', resetCanvasOverlayState);
  editor.on('canvas:frame:load', resetCanvasOverlayState);

  // Expose loader controls as commands so callers (e.g. the website
  // dropdown in PageTemplateService) can drive the loader without
  // importing this module directly.
  editor.Commands.add('pb-show-canvas-loader', {
    run: forceShowCanvasOverlay,
  });
  editor.Commands.add('pb-suppress-canvas-loader', {
    run: suppressCanvasOverlay,
  });
  // Callers that know they're triggering a long-running data fetch +
  // re-render should acquire the lock before starting the fetch and
  // release it in .finally(). While locked, no settle-observer or
  // max-timer can dismiss the loader — only the eventual unlock followed
  // by the new components settling will.
  editor.Commands.add('pb-lock-canvas-loader', {
    run: lockCanvasOverlay,
  });
  editor.Commands.add('pb-unlock-canvas-loader', {
    run: unlockCanvasOverlay,
  });

  // Main React component
  domc.addType('react-component', {
    model: {
      toHTML(opts = {}) {
        return defModel.prototype.toHTML.call(this, {
          ...opts,
          tag: this.get('type')
        });
      }
    },
    view: {
      tagName: 'div',

      init() {
        const { model } = this;
        this.listenTo(model, 'add remove', this.hanldeComponentsChange);
        // this.listenTo(model, 'change:attributes', this.render);
        // this.listenTo(model, 'change:style ', this.updateWidth);
        // this.listenTo(model.components(), 'add remove reset', this.__upRender);
        // this.listenTo('trait:update', this.handleTraitsUpdate(model))
      },

      hanldeComponentsChange() {
        try {
          const componentName = this.model.attributes.component.name;
          let iframes = document.getElementsByClassName("gjs-frame");
          if (iframes) {
            let iframeHead = iframes[0].contentWindow.document.head;
            if (iframeHead) {
              let existingStyles = iframeHead.getElementsByTagName("style");
              for (let eStyle of existingStyles) {
                if (eStyle.getAttribute("name") && eStyle.getAttribute("name") == componentName) {
                  eStyle.parentNode.removeChild(eStyle);
                  break;
                }
              }
            }
          }
        } catch (error) {
          console.log(error);
        }
      },

      updateWidth() {
        model.setStyle({
          width: parseFloat(selectedWidth, 10) / containerWidth * 100 + '%'
        })
        //...
      },

      getChildrenContainer() {
        const { childrenContainer } = this;
        if (childrenContainer) return childrenContainer;

        this.childrenContainer = document.createElement('childc');

        return this.childrenContainer;
      },

      /**
       * We need this container to understand if the React component is able
       * to render children
       */
      createReactChildWrap() {
        return React.createElement('span', { [wrpChld]: true });
      },

      createReactEl(cmp, props) {
        return React.createElement(cmp, props, this.createReactChildWrap());
      },

      mountReact(cmp, el) {
        //createRoot(el).render(cmp);
        ReactDOM.render(cmp, el);
      },

      traitUpdate(value) {
        const i_component = value.component;
        const trait = value.trait;
        const ElmToRerender = i_component.attributes.component;
        window.gpEditor.Panels.getButton("options", "publishBtn").set("active", false);
        window.gpEditor.Panels.getButton("options", "savebtn").set("active", false);

        if(trait.attributes.name=='isPreview'||trait.attributes.name.includes("isPreview")) {
          i_component.attributes.attributes.ENDPOINTS.forEach(t=>{t.preview=trait.attributes.value;});
        }

        if (ElmToRerender) {
          i_component.attributes.attributes[trait.attributes.name] = trait.attributes.value;
          editor.store();

          let ElmRender = <ElmToRerender {...i_component.attributes.attributes} />
          if (i_component.view)
            ReactDOM.render(ElmRender, i_component.view.el);
        }
      },

      render() {

        editor.DomComponents.getComponent().get('components').forEach( mod => {
          
          let classes = JSON.parse(JSON.stringify(mod.attributes.classes));

          if( classes.includes("non-removeable") ) {
            const toolbar = mod.get('toolbar') || [];
            const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-delete' );
            mod.set({removable: false});
            mod.set('toolbar', filteredToolbar);
          }
          mod.forEachChild( c => {
            let classes = JSON.parse(JSON.stringify(c.attributes.classes));
            // console.log("classesclasses22", classes);
            if( classes.includes("non-removeable") ) {
              const toolbar = c.get('toolbar') || [];
              const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-delete' );
              c.set({removable: false});
              c.set('toolbar', filteredToolbar);
            }
          } );

          if( classes.includes("non-moveable") ) {
            const toolbar = mod.get('toolbar') || [];
            const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-move' );
            mod.set({draggable: false});
            mod.set('toolbar', filteredToolbar);
          }
          mod.forEachChild( c => {
            let classes = JSON.parse(JSON.stringify(c.attributes.classes));
            if( classes.includes("non-moveable") ) {
              const toolbar = c.get('toolbar') || [];
              const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-move' );
              c.set({draggable: false});
              c.set('toolbar', filteredToolbar);
            }
          } );

          if( classes.includes("non-cloneable") ) {
            const toolbar = mod.get('toolbar') || [];
            const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-clone' );
            mod.set({copyable: false});
            mod.set('toolbar', filteredToolbar);
          }
          mod.forEachChild( c => {
            let classes = JSON.parse(JSON.stringify(c.attributes.classes));
            if( classes.includes("non-cloneable") ) {
              const toolbar = c.get('toolbar') || [];
              const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-clone' );
              c.set({copyable: false});
              c.set('toolbar', filteredToolbar);
            }
          } );

        } );

        editor.DomComponents.getComponent().get('components').forEach( mod => {
          let classes = JSON.parse(JSON.stringify(mod.attributes.classes));

          if( classes.includes("non-removeable") ) {
            const toolbar = mod.get('toolbar') || [];
            const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-delete' );
            mod.set({removable: false});
            mod.set('toolbar', filteredToolbar);
          }
          mod.forEachChild( c => {
            let classes = JSON.parse(JSON.stringify(c.attributes.classes));
            if( classes.includes("non-removeable") ) {
              const toolbar = c.get('toolbar') || [];
              const filteredToolbar = toolbar.filter( t => t.command !== 'tlb-delete' );
              c.set({removable: false});
              c.set('toolbar', filteredToolbar);
            }
          } );
        } );

        this.model.traits.forEach(tr => {
          tr.setValue(this.model.attributes.attributes[tr.getName()]);
        });

        setInterval(() => {
          if( stackTimeInterval && stackTimeInterval.length > 0 ) {
            //clearInterval(stackTimeInterval[0]);
            //stackTimeInterval = [];
          }
        }, 200);

        stackTimeInterval = [];
        this.em.on('trait:update', (value) => {
          let remoteTraitIds = localStorage.getItem("traitIds");
          if( remoteTraitIds ) {
            remoteTraitIds = JSON.parse(remoteTraitIds);
            let i_cid = value.trait.cid;
            if( remoteTraitIds.indexOf(i_cid) == -1 ) {

              if( !runningUpdateStack || undefined !== runningUpdateStack[i_cid] && runningUpdateStack[i_cid] ) {
                runningUpdateStack[i_cid] = value;
                
                if( !stackTimeInterval || stackTimeInterval.length == 0 ) {
                  stackTimeInterval[0] = setInterval(() => {
                    if( runningUpdateStack && lastExecutedAt < new Date().valueOf() - 300 ) {
                      for( let i__cid in runningUpdateStack ) {
                        lastExecutedAt = new Date().valueOf();
                        this.traitUpdate(runningUpdateStack[i__cid]);
                        delete runningUpdateStack[i__cid];
                      }
                      
                      clearInterval(stackTimeInterval[0]);
                      stackTimeInterval = [];
                    }
                  }, 160);
                }
                
              } else {

                for( let i__cid in runningUpdateStack ) {
                  lastExecutedAt = new Date().valueOf();
                  this.traitUpdate(runningUpdateStack[i__cid]);
                  delete runningUpdateStack[i__cid];
                }

                runningUpdateStack[i_cid] = value;
              }
            } 
          }
        });

        //   const updateTrait = debounce((value, editor) => {
        //     const i_component = value.component;
        //     const trait = value.trait;
        //     const ElmToRerender = i_component.attributes.component;
        //     window.gpEditor.Panels.getButton("options", "publishBtn").set("active", false);
        //     window.gpEditor.Panels.getButton("options", "savebtn").set("active", false);

        //     if (ElmToRerender) {
        //         //console.log('bbbbbb')
        //         i_component.attributes.attributes[trait.attributes.name] = trait.attributes.value;
        //         editor.store();

        //         let ElmRender = <ElmToRerender {...i_component.attributes.attributes} />
        //         if (i_component.view)
        //             ReactDOM.render(ElmRender, i_component.view.el);
        //     }
        // }, 300); // Adjust debounce time as needed (in milliseconds)

        // Attach the debounced function to the event listener
        // this.em.on('trait:update', (value) => {
        //     updateTrait(value, editor);
        // });

        this.em.on('component:remove', component => {
            const traits = component.get('traits');
            let localTraitIds = [];
            traits.each(trait => {
              localTraitIds.push(trait.cid);
            });

            let remoteTraitIds = localStorage.getItem("traitIds");
            if( !remoteTraitIds ) {
              remoteTraitIds = [];
            } else {
              remoteTraitIds = JSON.parse(remoteTraitIds);
            }

            localStorage.setItem( "traitIds", JSON.stringify([...new Set(remoteTraitIds.concat(localTraitIds))]));

        });

        this.em.Commands.add('tlb-clone', {
          run(editor, sender, opts) {
              const selected = editor.getSelected();
              if (!selected) return;
              const cloned = selected.clone();
              cloned.addAttributes({ 'data-cloned': 'true' });
              const toolbar = cloned.get('toolbar') || [];
              const filteredToolbar = toolbar.filter( t => !(t.command == 'tlb-delete' || t.command == 'tlb-move') );
              filteredToolbar.push( {attributes: { class: 'fa fa-trash' },command: 'tlb-delete'} );
              filteredToolbar.push( {attributes: { class: 'fa fa-arrows' },command: 'tlb-move'} );
              cloned.set('toolbar', filteredToolbar);
              cloned.set({removable: true, draggable: true});
              cloned.removeClass('non-removeable');
              cloned.removeClass('non-moveable');

              const parent = selected.parent();
              const index = parent.components().indexOf(selected);
              parent.components().add(cloned, { at: index + 1 });
          }
        });

        this.em.on('component:add', component => {

          setTimeout(() => {
            const traits = component.get('traits');
            let localTraitIds = [];
            traits.each(trait => {
              localTraitIds.push(trait.cid);
            });

            let remoteTraitIds = localStorage.getItem("traitIds");
            if( !remoteTraitIds ) {
              remoteTraitIds = [];
            } else {
              remoteTraitIds = JSON.parse(remoteTraitIds);
            }
            localStorage.setItem( "traitIds", JSON.stringify(remoteTraitIds.filter(x => !localTraitIds.includes(x))));
            
            if (component.attributes && component.attributes.attributes && component.attributes.attributes.layout && component.attributes.attributes.name) {
              let layout = component.attributes.attributes.layout;
              if (layout) {
                updateSessionObject('layoutMetaData', layout);
                let i_css = getFormattedCss(layout.css);
                loadStyles(window.gpEditor, i_css);
              }
            }
            if (component.attributes && (component.attributes.type == 'Ad Scripts' || component.attributes.type == 'Ad Scripts Two')) {
              if (typeof component.attributes.attributes.compHeight === 'undefined')
                component.attributes.attributes.compHeight = component.view.el.offsetHeight
              if (typeof component.attributes.attributes.compWidth === 'undefined')
                component.attributes.attributes.compWidth = component.view.el.offsetWidth
              if (typeof component.attributes.attributes.parentHeight === 'undefined')
                component.attributes.attributes.parentHeight = component.parent().view.el.offsetHeight
              if (typeof component.attributes.attributes.parentWidth === 'undefined')
                component.attributes.attributes.parentWidth = component.parent().view.el.offsetWidth
            }
            //add the global settings if it is not present
            // if (editor.getComponents().filter((global) => global.attributes && global.attributes.tagName == 'globalsettingcomponent').length == 0) {
            //   editor.runCommand('save-global-command');
            // }
            // if(component.attributes && component.attributes.attributes && window.gpEditor.getComponents()){
            //   window.gpEditor.getComponents().forEach((global) => {
            //     if(global.attributes && global.attributes.tagName == 'globalsettingcomponent'){
            //       const GLOBAL_DATA = global.attributes.attributes.GLOBAL_DATA;
            //       component.addAttributes({ GLOBAL_DATA });
            //     }
            //   });
            // }

          }, 300);

        });

        this.em.on('component:selected', (ref) => {
          const comp = ref.parents().filter(e => e.attributes.attributes.type == "LAYOUT")[0];
          if (comp) {
            comp.view.el.click();
          } else {
            if (ref.get("type")) {
              ref.em.Panels.getButton('views', 'open-tm_1').set('active', true);
              document.querySelector(".gjs-traits-label").innerHTML = ref.attributes.type ? ref.attributes.type + " Properties" : "Component Settings"
            } else if ( ! (ref.get('attributes') && ref.get('attributes')['data-cloned'] !== undefined && ref.get('attributes')['data-cloned']) ) {
              window.gpEditor && window.gpEditor.runCommand("core:component-enter")
            }
            if (ref.attributes.type == "Page Not Found") {
              setTimeout(() => {
                document.querySelector(".gjs-trt-traits").innerHTML = "This is a predefined component";
              })
            }
          }
        });

        let compName = this.model.attributes.component.name ? this.model.attributes.component.name : this.model.attributes.type;
        if (compName) {
          compName = compName.replace(/ /g, "");
        }

        if (STYLES && STYLES[compName] !== undefined && STYLES[compName]) {
          let stylesheets = STYLES[compName];
          stylesheets.forEach((sheet, index) => {
            appendStyleSheet(compName + '' + index, sheet);
          });
        }

        const { model, el } = this;
        this.updateAttributes();
        this.renderChildren();

        // Cover the canvas while components asynchronously fetch data and
        // their heights settle. The overlay fades out after every component
        // observer reports a stable height and a short quiet period.
        installCanvasOverlay();

        const reactEl = this.createReactEl(model.get('component'), {
          ...model.get('attributes')
        });
        this.mountReact(reactEl, el);

        observeHeightSettle(el);
        const chld = el.querySelector(`span[${wrpChld}]`);

        // If the container is found, the react component is able to render children
        if (chld) {
          const chldCont = this.getChildrenContainer();
          while (chldCont.firstChild) {
            chld.appendChild(chldCont.firstChild);
          }
        }
        return this;
      },

      // Clean up React component on removal
      onRemove() {
        ReactDOM.unmountComponentAtNode(this.el);
      },

      __upRender() {
        clearTimeout(this._upr);
        this._upr = setTimeout(() => this.render());
      }
    }
  });
};
